import { FormattedMessage, useIntl } from 'react-intl';
import React from 'react';
import bemify from 'bemify-js';

import { StaticImage } from '@zapier/common-components';

import Code from '../components/Code';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';
import SyntaxHighlighter from '../components/SyntaxHighlighter';

const bem = bemify('create-action');

const jsCodeBlock = `const sample = require('../samples/sample_issue');

const createIssue = (z, bundle) => {
  const responsePromise = z.request({
    method: 'POST',
    // Defining the interaction with your API endpoint. The bundle
    // parameter holds data input and authentication information
    // from the Zap:
    url: \`https://api.github.com/repos/\${bundle.inputData.repo}/issues\`,
    body: JSON.stringify({
    title: bundle.inputData.title
    })
  });
  return responsePromise
    .then(response => JSON.parse(response.content));
};

module.exports = {
  key: 'issue',
  noun: 'Issue',

display: {
  // What the user will see in the Zap Editor when selecting an action
  label: 'Create Issue',
  description: 'Creates an issue.'
},

operation: {
   // Data users will be asked to set in the Zap Editor
  inputFields: [
      {key: 'repo', label: 'Repo', required: true,
      // Creates a dynamic drop-down that's populated by the 'repo' trigger
      dynamic: 'repo.full_name.full_name'},
      {key: 'title', label:'Title', required: true},
      {key: 'body', label:'Body', required: false}
    ],
    perform: createIssue,
    // Sample data that the user will see if they skip the test
    // step in the Zap Editor
    sample: sample
  }
}`;

const CreateAction = () => {
  const intl = useIntl();

  return (
    <div className={bem()}>
      <StepTitle>
        <FormattedMessage
          defaultMessage="Explore the Project: A Create Action"
          id="headings-explore-project-create-action"
        />
      </StepTitle>
      <Paragraph>
        <FormattedMessage
          defaultMessage="You'll notice a couple of folders called"
          id="descriptions-create-and-trigger-folders-prefix"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="/creates"
            id="labels-creates-folder-path"
          />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="and"
          id="descriptions-create-and-trigger-folders-connector"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="/triggers"
            id="labels-triggers-folder-path"
          />
        </Code>
        <FormattedMessage
          defaultMessage=". Unsurprisingly, these are where the code for your create actions, and triggers live."
          id="descriptions-create-and-trigger-folders-explanation"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage='"Creates" are how you get data from a Zap to your API - think'
          id="descriptions-creates-http-actions-prefix"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="POST"
            id="labels-http-method-post"
          />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="or"
          id="descriptions-http-methods-connector"
        />{' '}
        <Code>
          <FormattedMessage defaultMessage="PUT" id="labels-http-method-put" />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="HTTP actions."
          id="descriptions-creates-http-actions-suffix"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage='"Triggers" are where you get data from your API,'
          id="descriptions-triggers-http-actions-prefix"
        />{' '}
        <Code>
          <FormattedMessage defaultMessage="GET" id="labels-http-method-get" />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="HTTP actions or web hooks."
          id="descriptions-triggers-http-actions-suffix"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage="The code here makes calls to your API endpoint and tells the Zapier platform how to create the Zap Editor form where users can configure their Zaps and map data to and from your API."
          id="descriptions-api-endpoint-and-zap-editor-form"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage="A quick look at our example create code in"
          id="descriptions-example-create-code-prefix"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="creates/issue.js"
            id="labels-example-create-code-file-path"
          />
        </Code>
        :
      </Paragraph>
      <SyntaxHighlighter language="javascript">{jsCodeBlock}</SyntaxHighlighter>
      <Paragraph>
        <FormattedMessage
          defaultMessage="And here's an example of what this will look like when a user configures it in the Zap Editor:"
          id="descriptions-zap-editor-configuration-example"
        />
      </Paragraph>
      <StaticImage
        alt={intl.formatMessage({
          id: 'captions-create-action-screenshot-alt',
          defaultMessage: 'create action screenshot',
        })}
        src="https://cdn.zappy.app/a24892690780d13b48bb219485cb766e.png"
        title={intl.formatMessage({
          id: 'captions-create-action-screenshot-title',
          defaultMessage: 'create action screenshot',
        })}
      />
    </div>
  );
};

export default CreateAction;
